// Modos del editor que vienen de cuentas y aulas (docs/AULAS.md, «Pantallas»):
// - /p/:id ajeno: solo lectura, con «Guardar una copia».
// - /tareas/:tid/trabajo: el alumno trabaja en su copia, que se guarda sola (PUT /api/submissions/:id).
// - /tareas/:tid/plantilla: el maestro edita la plantilla, que se guarda sola (PUT /api/assignments/:id).
// - /entregas/:sid: el maestro revisa una entrega; puede simular y mover cosas, pero nada se guarda.
// Si el aula está archivada (classArchived), todo queda en solo lectura: no se trabaja, no se cambia la plantilla y no
// se califica ni se devuelve, pero se puede ver y simular.
// Cada modo pinta la franja bajo la barra superior y oculta del menú Archivo lo que no aplica.
import { api } from '../api.js';
import { escapeHtml, linkify, fmtDateTime, fmtRelative, fmtNum } from './html.js';
import { submissionState, canEdit, canGrade, gradeText, isOverdue } from '../aulas.js';
import { blankProject } from '../blank.js';
import { openModal } from './dialogs.js';
import { loginUrl } from '../session.js';

const $ = (s) => document.querySelector(s);
const TASK_KINDS = ['work', 'template', 'review', 'error'];
export const isTaskMode = (app) => TASK_KINDS.includes(app.kind);

const ICON = {
  lock: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M7 10V7a5 5 0 0 1 10 0v3h1a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2zm2 0h6V7a3 3 0 0 0-6 0z"/></svg>',
  task: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 2h6a1 1 0 0 1 1 1v1h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2V3a1 1 0 0 1 1-1zm1 2v2h4V4zm5.3 6.3-4.3 4.3-2.3-2.3-1.4 1.4 3.7 3.7 5.7-5.7z"/></svg>',
  edit: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m14.1 5.1 4.8 4.8L9 19.8H4.2V15zm1.4-1.4 1.7-1.7a1.5 1.5 0 0 1 2.1 0l2.7 2.7a1.5 1.5 0 0 1 0 2.1l-1.7 1.7z"/></svg>',
  eye: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5c5 0 9 4.5 10 7-1 2.5-5 7-10 7S3 14.5 2 12c1-2.5 5-7 10-7zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm0 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4z"/></svg>',
  prev: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15.4 6.4 14 5l-7 7 7 7 1.4-1.4L9.8 12z"/></svg>',
  next: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.6 6.4 10 5l7 7-7 7-1.4-1.4 5.6-5.6z"/></svg>',
  close: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6.4 5 5 6.4 10.6 12 5 17.6 6.4 19l5.6-5.6 5.6 5.6 1.4-1.4-5.6-5.6L19 6.4 17.6 5 12 10.6z"/></svg>',
};

// Aula archivada (docs/AULAS.md): el motivo de la solo lectura en cada modo. En la franja, «desarchívala» lleva a los
// ajustes del aula.
const ARCHIVED = {
  work: 'El aula está archivada: ya no se pueden hacer ni entregar tareas. Puedes ver tu trabajo y simularlo.',
  template: 'El aula está archivada: desarchívala para cambiar la plantilla. Puedes verla y simularla.',
  review: 'El aula está archivada: desarchívala para calificar o devolver. Puedes ver la entrega y simularla.',
};

// Datos de la tarea abierta: { tid, sub, assignment, className, list, error, retry }.
let task = {};

export function setupModes(app) {
  app.on('mode', () => render(app));
  app.on('read-only', () => render(app));
  app.on('user', () => render(app));
  app.on('save-state', () => {
    const el = $('#task-strip [data-save-status]');
    if (!el) return;
    const s = app.saveStatus();
    el.textContent = s.text;
    el.title = s.title;
    el.className = 'strip-save ' + s.cls;
  });
  // No se pudo guardar (409): otra ventana entregó, el maestro calificó o el aula se archivó mientras se editaba. Se
  // vuelve a leer el estado.
  app.on('task-conflict', (e) => (app.kind === 'template' ? refreshTemplate(app, e) : refreshWork(app)));

  $('#task-strip').addEventListener('click', (e) => {
    const b = e.target.closest('[data-strip]');
    if (b) stripAction(app, b.dataset.strip, b);
  });
  $('#grade-panel').addEventListener('click', (e) => {
    const b = e.target.closest('[data-strip]');
    if (b) stripAction(app, b.dataset.strip, b);
  });
  $('#grade-panel').addEventListener('submit', (e) => {
    e.preventDefault();
    gradeSubmission(app, 'grade');
  });
  render(app);
}

// Arranque de cada modo ----------------------------------------------------------------------------------
/** Abre /tareas/:tid/trabajo, /tareas/:tid/plantilla o /entregas/:sid. */
export async function startTask(app, kind, id) {
  if (!app.user) {
    app.leaving = true;
    location.replace(loginUrl(location.pathname));
    return;
  }
  try {
    if (kind === 'work') await startWork(app, id);
    else if (kind === 'template') await startTemplate(app, id);
    else await startReview(app, id);
  } catch (e) {
    if (e.status === 401) {
      app.leaving = true;
      location.replace(loginUrl(location.pathname));
      return;
    }
    showError(app, e);
  }
}

function enter(app, kind, { saveHandler = null, draftKey = null, copyName = null, readOnly = '', title = '' } = {}) {
  app.resetMode();
  app.kind = kind;
  app.saveHandler = saveHandler;
  app.draftKey = draftKey;
  app.copyName = copyName;
  app.displayName = title;
  app.setReadOnly(readOnly);
}

async function startWork(app, tid) {
  let sub;
  let a;
  try {
    [sub, a] = await Promise.all([api.post(`/api/assignments/${tid}/start`), api.get(`/api/assignments/${tid}`)]);
  } catch (e) {
    // El maestro dueño no tiene «trabajo»: su lugar es la plantilla.
    if (e.status === 403 && app.user.role === 'teacher') return location.replace(`/tareas/${tid}/plantilla`);
    throw e;
  }
  task = { tid, sub, assignment: { ...sub.assignment, title: a.title, instructions: a.instructions, points: a.points, dueAt: a.dueAt } };
  const archived = !!sub.assignment.classArchived;
  enter(app, 'work', {
    saveHandler: (project, opts) => api.put(`/api/submissions/${sub.id}`, { project }, opts),
    draftKey: 'sub:' + sub.id,
    copyName: a.title,
    readOnly: lockReason(sub.state, archived),
    title: a.title,
  });
  app.load(sub.project, { url: location.pathname });
  if (canEdit(sub.state) && !archived) restoreDraft(app, sub.updatedAt);
  app.emit('mode');
  // La primera vez se muestran las instrucciones.
  if (!archived && !sub.submittedAt && sub.state === 'working' && a.instructions && !seen('ins:' + sub.id)) showInstructions(app);
}

async function startTemplate(app, tid) {
  const a = await api.get(`/api/assignments/${tid}`);
  // Solo el dueño recibe la plantilla; un alumno va a su trabajo.
  if (!a.template) return location.replace(`/tareas/${tid}/trabajo`);
  const c = await api.get(`/api/classes/${a.classId}`);
  task = { tid, assignment: a, className: c.name };
  enter(app, 'template', {
    saveHandler: (template, opts) => api.put(`/api/assignments/${tid}`, { template }, opts),
    draftKey: 'tpl:' + tid,
    copyName: a.title,
    readOnly: a.classArchived ? ARCHIVED.template : '',
    title: a.title,
  });
  app.load(a.template, { url: location.pathname });
  if (!a.classArchived) restoreDraft(app, a.updatedAt);
  app.emit('mode');
}

async function startReview(app, sid) {
  const sub = await api.get(`/api/submissions/${sid}`);
  // Un alumno que abre el enlace de su entrega va a su trabajo.
  if (app.user.role !== 'teacher') return location.replace(`/tareas/${sub.assignment.id}/trabajo`);
  const list = await api.get(`/api/assignments/${sub.assignment.id}/submissions`);
  task = { sub, assignment: sub.assignment, list: list.filter((s) => s.id) };
  enter(app, 'review', { copyName: `${sub.assignment.title} - ${sub.student.name}`, title: sub.assignment.title });
  app.load(sub.project, { url: location.pathname });
  // En pantalla ancha el panel de calificación empieza abierto si hay algo que calificar.
  document.body.classList.toggle('grading', window.innerWidth > 820 && canGrade(sub.state) && !sub.assignment.classArchived);
  app.emit('mode');
}

/** Pasa a otra entrega de la misma tarea sin recargar la página (anterior / siguiente). */
async function goToSubmission(app, sid) {
  let sub;
  try {
    sub = await api.get(`/api/submissions/${sid}`);
  } catch (e) {
    app.toast('No se pudo abrir la entrega: ' + e.message, true);
    return;
  }
  task.sub = sub;
  task.assignment = sub.assignment;
  app.copyName = `${sub.assignment.title} - ${sub.student.name}`;
  app.load(sub.project, { url: `/entregas/${sid}` });
  app.emit('mode');
}

function showError(app, e) {
  // 409: la tarea es de un aula archivada y no se había empezado (el mensaje del servidor lo dice); reintentar no sirve.
  task = e.status === 409
    ? { error: e.message, retry: false }
    : { error: e.status === 404 || e.status === 403 ? `No encontramos esta tarea o no tienes acceso con la cuenta de ${app.user.name}.` : `No se pudo abrir: ${e.message}` };
  enter(app, 'error', { readOnly: 'No hay nada que editar: no se pudo abrir la tarea.', title: 'Tarea' });
  app.load(blankProject(), { url: location.pathname });
  app.emit('mode');
}

function lockReason(state, archived = false) {
  if (archived) return ARCHIVED.work;
  if (state === 'submitted') return 'Ya entregaste: anula la entrega para hacer cambios.';
  if (state === 'graded') return 'Tu trabajo ya está calificado: puedes verlo y simularlo, pero no cambiarlo.';
  return '';
}

/** Borrador local más nuevo que lo guardado (la página se cerró antes de guardar): se recupera y se guarda. */
function restoreDraft(app, updatedAt) {
  let d = null;
  try {
    d = JSON.parse(localStorage.getItem('arduino3d.draft.' + app.draftKey) || 'null');
  } catch {
    d = null;
  }
  if (!d || !d.project || !(d.at > (Date.parse(updatedAt) || 0) + 1000)) return;
  app.load(d.project, { url: location.pathname, keepView: true });
  app.markDirty();
  app.toast('Se recuperaron cambios que no se alcanzaron a guardar.');
}

function seen(key) {
  try {
    if (sessionStorage.getItem('arduino3d.seen.' + key)) return true;
    sessionStorage.setItem('arduino3d.seen.' + key, '1');
  } catch {
    // sin almacenamiento: se muestran siempre
  }
  return false;
}

// Franja bajo la barra -----------------------------------------------------------------------------------
function render(app) {
  const strip = $('#task-strip');
  const html = stripHtml(app);
  strip.hidden = !html;
  strip.className = 'task-strip ' + app.kind;
  strip.innerHTML = html;
  document.body.classList.toggle('has-strip', !!html);
  if (app.kind !== 'review') document.body.classList.remove('grading');
  renderGradePanel(app);
  updateMenus(app);
  app.emit('save-state');
}

function stripHtml(app) {
  const e = escapeHtml;
  const save = '<span class="strip-save" data-save-status></span>';
  if (app.kind === 'foreign') {
    const who = app.owner && app.owner.name ? `de ${e(app.owner.name)}` : 'de otra persona';
    return `<div class="strip-row"><span class="strip-icon">${ICON.lock}</span><span class="strip-title">Solo lectura</span>
      <span class="strip-text">Proyecto ${who}. Puedes simularlo; para cambiarlo, guarda una copia${app.user ? '' : ' (necesitas una cuenta)'}.</span>
      <span class="strip-actions"><button class="btn small primary" data-strip="copy">Guardar una copia</button></span></div>`;
  }
  if (app.kind === 'error') {
    return `<div class="strip-row"><span class="strip-icon">${ICON.task}</span><span class="strip-title">${e(task.error)}</span>
      <span class="strip-actions">${task.retry === false ? '' : '<button class="btn small" data-strip="retry">Reintentar</button>'}<a class="btn small primary" href="/inicio" data-leave>Ir al inicio</a></span></div>`;
  }
  const a = task.assignment;
  if (app.kind === 'work') {
    const s = task.sub;
    const st = submissionState(s, a.points);
    const due = a.dueAt
      ? `<span class="strip-meta${isOverdue(a.dueAt) && canEdit(s.state) ? ' overdue' : ''}" title="${e(fmtDateTime(a.dueAt))}">Fecha límite: ${e(fmtDateTime(a.dueAt))}</span>`
      : '<span class="strip-meta">Sin fecha límite</span>';
    const action = a.classArchived
      ? ''
      : canEdit(s.state)
        ? `<button class="btn small primary" data-strip="submit">${s.state === 'returned' ? 'Volver a entregar' : 'Entregar'}</button>`
        : s.state === 'submitted'
          ? '<button class="btn small" data-strip="unsubmit">Anular entrega</button>'
          : '';
    const feedback = (s.state === 'graded' || s.state === 'returned') && (s.grade !== null || s.comment)
      ? `<div class="strip-row feedback">${s.grade !== null && s.grade !== undefined ? `<b class="strip-grade">Calificación: ${e(gradeText(s.grade, a.points))}</b>` : ''}
          ${s.comment ? `<span class="strip-comment" title="${e(s.comment)}">${e(s.comment)}</span><button class="btn small ghost" data-strip="instructions">Ver comentario</button>` : ''}</div>`
      : '';
    return `<div class="strip-row"><span class="strip-icon">${ICON.task}</span>
        <span class="strip-title" title="${e(a.title)}">${e(a.title)}</span><span class="strip-meta hide-narrow">${e(a.className || '')}</span>
        ${due}<span class="state ${st.cls}">${e(st.label)}</span>${save}
        <span class="strip-actions"><button class="btn small" data-strip="instructions">Instrucciones</button>${action}</span></div>${feedback}${archivedRow(app)}`;
  }
  if (app.kind === 'template') {
    const started = a.counts ? a.counts.started : 0;
    const note = a.classArchived
      ? ''
      : started
        ? `<span class="strip-text strip-warn">${started === 1 ? 'Un alumno ya empezó' : `${started} alumnos ya empezaron`}: los cambios no llegan a sus copias.</span>`
        : '<span class="strip-text hide-narrow">Lo que dejes aquí es con lo que empieza cada alumno.</span>';
    const done = a.classArchived
      ? `<a class="btn small" href="/aulas/${e(a.classId)}/tareas/${e(a.id)}" data-leave>Volver a la tarea</a>`
      : '<button class="btn small primary" data-strip="done">Listo</button>';
    return `<div class="strip-row"><span class="strip-icon">${ICON.edit}</span>
        <span class="strip-title" title="Plantilla de «${e(a.title)}»">Plantilla de «${e(a.title)}»</span><span class="strip-meta hide-narrow">${e(task.className || '')}</span>
        ${note}${save}<span class="strip-actions">${done}</span></div>${archivedRow(app)}`;
  }
  if (app.kind === 'review') {
    const s = task.sub;
    const st = submissionState(s, a.points);
    const i = task.list.findIndex((x) => x.id === s.id);
    const n = task.list.length;
    const grading = document.body.classList.contains('grading');
    // Con el aula archivada el panel solo muestra la calificación y el comentario que ya tiene.
    const label = a.classArchived ? 'Calificación' : 'Calificar';
    return `<div class="strip-row"><span class="strip-icon">${ICON.eye}</span>
        <span class="strip-nav"><button class="btn small icon" data-strip="prev" aria-label="Entrega anterior" title="Entrega anterior"${i > 0 ? '' : ' disabled'}>${ICON.prev}</button>
          <span class="strip-title" title="${e(s.student.name)}">${e(s.student.name)}</span><span class="strip-meta">${i + 1} de ${n}</span>
          <button class="btn small icon" data-strip="next" aria-label="Entrega siguiente" title="Entrega siguiente"${i >= 0 && i < n - 1 ? '' : ' disabled'}>${ICON.next}</button></span>
        <span class="strip-meta hide-narrow" title="${e(a.className || '')}">${e(a.title)}</span><span class="state ${st.cls}">${e(st.label)}</span>
        ${s.submittedAt ? `<span class="strip-meta hide-narrow">Entregada ${e(fmtRelative(s.submittedAt))}</span>` : ''}
        ${save}<span class="strip-actions"><button class="btn small${grading ? ' active' : a.classArchived ? '' : ' primary'}" data-strip="grade-toggle" aria-controls="grade-panel" aria-expanded="${grading}">${label}</button>
          <a class="btn small" href="/aulas/${e(a.classId)}/tareas/${e(a.id)}" data-leave>Volver a la tarea</a></span></div>${archivedRow(app)}`;
  }
  return '';
}

/** Aviso de aula archivada bajo la franja (o nada). Al maestro, «desarchívala» lo lleva a los ajustes del aula. */
function archivedRow(app) {
  const a = task.assignment;
  if (!a || !a.classArchived) return '';
  return `<div class="strip-row archived"><span class="strip-icon">${ICON.lock}</span><span class="strip-text">${archivedHtml(app.kind)}</span></div>`;
}

function archivedHtml(kind, text = ARCHIVED[kind]) {
  const a = task.assignment;
  return escapeHtml(text).replace('desarchívala', `<a href="/aulas/${escapeHtml(a.classId)}#ajustes" data-leave>desarchívala</a>`);
}

/** Menú Archivo y botones de la barra según el modo: dentro de una tarea no hay «Proyecto nuevo», etc. */
function updateMenus(app) {
  const k = app.kind;
  const inTask = TASK_KINDS.includes(k);
  const set = (action, visible, label) => {
    const b = document.querySelector(`.menu-list [data-action="${action}"]`);
    if (!b) return;
    b.hidden = !visible;
    if (label) b.firstChild.textContent = label + ' ';
  };
  set('new', !inTask);
  set('open', !inTask);
  set('save', k === 'project' || ((k === 'work' || k === 'template') && !app.readOnly), k === 'project' ? 'Guardar' : 'Guardar ahora');
  set('save-as', k !== 'error', k === 'project' ? 'Guardar como copia' : k === 'foreign' ? 'Guardar una copia' : 'Guardar una copia en Mis proyectos');
  set('link', !inTask, k === 'foreign' ? 'Copiar enlace' : 'Compartir…');
  set('import', !inTask);
  set('wokwi', !inTask);
  const catalog = document.querySelector('.stage-toolbar [data-action="catalog"]');
  if (catalog) {
    catalog.disabled = !!app.readOnly;
    catalog.title = app.readOnly ? app.readOnly : '';
  }
  const tab = document.querySelector('.mobile-tabs [data-pane="grade"]');
  if (tab) {
    tab.hidden = k !== 'review';
    tab.textContent = k === 'review' && task.assignment.classArchived ? 'Calificación' : 'Calificar';
  }
}

// Acciones de la franja y del panel -------------------------------------------------------------------------
async function stripAction(app, action, button) {
  switch (action) {
    case 'copy':
      return app.saveCopy();
    case 'retry':
      return location.reload();
    case 'instructions':
      return showInstructions(app);
    case 'submit':
      return submitWork(app, button);
    case 'unsubmit':
      return unsubmitWork(app, button);
    case 'done': {
      button.disabled = true;
      const ok = await app.flushSave();
      button.disabled = false;
      if (!ok && !confirm('No se pudieron guardar los últimos cambios de la plantilla. ¿Salir de todos modos?')) return;
      app.leaving = true;
      location.href = `/aulas/${task.assignment.classId}/tareas/${task.assignment.id}`;
      return;
    }
    case 'prev':
    case 'next': {
      const i = task.list.findIndex((x) => x.id === task.sub.id) + (action === 'next' ? 1 : -1);
      if (task.list[i]) return goToSubmission(app, task.list[i].id);
      return;
    }
    case 'grade-toggle': {
      if (window.innerWidth <= 820) return showPane('grade');
      document.body.classList.toggle('grading');
      render(app);
      if (document.body.classList.contains('grading')) $('#gp-grade')?.focus();
      return;
    }
    case 'grade-close':
      if (window.innerWidth <= 820) return showPane('stage');
      document.body.classList.remove('grading');
      render(app);
      return;
    case 'return':
      return gradeSubmission(app, 'return');
  }
}

/** En celular: cambia a una pestaña (Código, Circuito, Monitor, Calificar). */
function showPane(name) {
  document.querySelector(`.mobile-tabs [data-pane="${name}"]`)?.click();
}

function showInstructions(app) {
  const a = task.assignment;
  const s = task.sub;
  const e = escapeHtml;
  const st = submissionState(s, a.points);
  const feedback = s && (s.state === 'graded' || s.state === 'returned')
    ? `<section class="ins-feedback ${e(s.state)}"><h3>${s.state === 'returned' ? 'Devuelta para corregir' : 'Calificación'}${s.grade !== null && s.grade !== undefined ? `: ${e(gradeText(s.grade, a.points))}` : ''}</h3>
        ${s.comment ? `<div class="text">${linkify(s.comment)}</div>` : '<p class="muted">Sin comentario.</p>'}</section>`
    : '';
  openModal(
    a.title,
    `<div class="ins-meta"><span>${e(a.className || '')}</span><span>${a.dueAt ? `Fecha límite: ${e(fmtDateTime(a.dueAt))}` : 'Sin fecha límite'}</span>
      <span>${e(fmtNum(a.points))} ${Number(a.points) === 1 ? 'punto' : 'puntos'}</span><span class="state ${st.cls}">${e(st.label)}</span></div>
     ${feedback}
     <h3 class="ins-h">Instrucciones</h3>
     ${a.instructions ? `<div class="text">${linkify(a.instructions)}</div>` : '<p class="muted">Tu maestro no escribió instrucciones para esta tarea.</p>'}
     <p class="muted small ins-how">${a.classArchived ? ARCHIVED.work : canEdit(s.state) ? 'Tu trabajo se guarda solo mientras lo editas. Cuando termines, presiona «Entregar».' : s.state === 'submitted' ? 'Ya la entregaste. Mientras no la califiquen puedes anular la entrega para hacer cambios.' : 'Tu trabajo ya está calificado: puedes verlo y simularlo, pero no cambiarlo.'}</p>`,
    { narrow: true },
  );
}

async function submitWork(app, button) {
  const again = task.sub.state === 'returned';
  if (!confirm(`¿${again ? 'Volver a entregar' : 'Entregar'} tu trabajo?\n\nDespués ya no podrás cambiarlo, a menos que anules la entrega antes de que lo califiquen.`)) return;
  button.disabled = true;
  // Primero se guarda lo último que se cambió: se entrega lo que se ve.
  const ok = await app.flushSave();
  if (!ok) {
    button.disabled = false;
    app.toast('No se pudo guardar tu trabajo, así que no se entregó. Revisa tu conexión e inténtalo otra vez.', true);
    return;
  }
  try {
    const s = await api.post(`/api/submissions/${task.sub.id}/submit`);
    applySubmission(app, s);
    app.toast(s.late ? 'Entregada, fuera de la fecha límite.' : '¡Entregada! Tu maestro ya puede revisarla.');
  } catch (e) {
    button.disabled = false;
    app.toast('No se pudo entregar: ' + e.message, true);
    refreshWork(app);
  }
}

async function unsubmitWork(app, button) {
  if (!confirm('¿Anular la entrega?\n\nTu trabajo vuelve a «En progreso» para que hagas cambios. Acuérdate de entregarlo otra vez.')) return;
  button.disabled = true;
  try {
    applySubmission(app, await api.post(`/api/submissions/${task.sub.id}/unsubmit`));
    app.toast('Entrega anulada: ya puedes hacer cambios.');
  } catch (e) {
    button.disabled = false;
    app.toast('No se pudo anular: ' + e.message, true);
    refreshWork(app);
  }
}

function applySubmission(app, s) {
  // La entrega completa (GET) trae su tarea: el aula pudo archivarse mientras tanto.
  if (s.assignment) task.assignment.classArchived = !!s.assignment.classArchived;
  task.sub = { ...task.sub, ...s, project: undefined };
  app.setReadOnly(lockReason(task.sub.state, task.assignment.classArchived));
  render(app);
}

async function refreshWork(app) {
  if (app.kind !== 'work') return;
  try {
    const s = await api.get(`/api/submissions/${task.sub.id}`);
    const was = task.sub.state;
    const archived = !task.assignment.classArchived && s.assignment.classArchived;
    applySubmission(app, s);
    if (archived) app.toast(ARCHIVED.work, true);
    else if (was !== s.state) app.toast(`Esta entrega cambió en otra ventana: ahora está «${submissionState(s, task.assignment.points).label}».`, true);
  } catch (e) {
    app.toast('No se pudo revisar el estado de la entrega: ' + e.message, true);
  }
}

/** La plantilla no se pudo guardar (409): su aula se archivó mientras se editaba. Queda en solo lectura. */
async function refreshTemplate(app, err) {
  let a = null;
  try {
    a = await api.get(`/api/assignments/${task.tid}`);
  } catch {
    // sin poder revisar, se dice el error del guardado
  }
  if (!a || !a.classArchived) return app.toast('No se pudo guardar: ' + err.message, true);
  task.assignment = { ...task.assignment, classArchived: true };
  app.setReadOnly(ARCHIVED.template);
  app.toast(err.message, true);
}

// Panel de calificación (revisión) -----------------------------------------------------------------------------
function renderGradePanel(app) {
  const panel = $('#grade-panel');
  if (app.kind !== 'review') {
    panel.hidden = true;
    panel.innerHTML = '';
    return;
  }
  panel.hidden = false;
  const s = task.sub;
  const a = task.assignment;
  const e = escapeHtml;
  const st = submissionState(s, a.points);
  if (a.classArchived) return renderArchivedPanel(panel, s, a, st);
  const ready = canGrade(s.state);
  // Se conserva lo que se estaba escribiendo si el panel se vuelve a pintar con la misma entrega.
  const keep = panel.dataset.sid === s.id ? { grade: $('#gp-grade')?.value, comment: $('#gp-comment')?.value } : null;
  panel.dataset.sid = s.id;
  panel.innerHTML = `<div class="grade-inner">
    <header><h2 id="gp-title">Calificar</h2><button class="btn icon ghost" type="button" data-strip="grade-close" aria-label="Cerrar el panel">${ICON.close}</button></header>
    <p class="gp-who"><b>${e(s.student.name)}</b> <span class="state ${st.cls}">${e(st.label)}</span></p>
    ${s.submittedAt ? `<p class="muted small">Entregada: ${e(fmtDateTime(s.submittedAt))}${s.late ? ' (fuera de la fecha límite)' : ''}</p>` : ''}
    ${ready ? '' : '<p class="notice">Aún no la entrega: podrás calificarla cuando la entregue. Mientras, puedes ver su avance.</p>'}
    <form id="grade-form" novalidate>
      <fieldset ${ready ? '' : 'disabled'}>
        <div class="field"><label for="gp-grade">Calificación <span class="muted">(de 0 a ${e(fmtNum(a.points))})</span></label>
          <div class="gp-grade"><input class="input" id="gp-grade" type="number" min="0" max="${e(a.points)}" step="0.1" inputmode="decimal" value="${e(keep ? keep.grade : s.grade ?? '')}" aria-describedby="gp-grade-help"><span>/ ${e(fmtNum(a.points))}</span></div>
          <span class="help" id="gp-grade-help">Con un decimal. Vacía, la entrega queda sin calificación.</span></div>
        <div class="field"><label for="gp-comment">Comentario para el alumno</label>
          <textarea class="input" id="gp-comment" rows="6" maxlength="5000">${e(keep ? keep.comment : s.comment || '')}</textarea></div>
        <p class="form-error notice error" role="alert"></p>
        <div class="gp-actions"><button class="btn primary" type="submit">Guardar calificación</button>
          <button class="btn" type="button" data-strip="return">Devolver para corregir</button></div>
      </fieldset>
    </form>
    <p class="muted small">«Devolver» le permite corregir y volver a entregar; ve la calificación y el comentario. Puedes cambiar la calificación cuando quieras.</p>
    ${s.gradedAt ? `<p class="muted small">Última calificación: ${e(fmtDateTime(s.gradedAt))}</p>` : ''}
  </div>`;
}

/** Aula archivada: el panel no ofrece calificar ni devolver; muestra la calificación y el comentario que ya tiene. */
function renderArchivedPanel(panel, s, a, st) {
  const e = escapeHtml;
  panel.dataset.sid = '';
  panel.innerHTML = `<div class="grade-inner">
    <header><h2 id="gp-title">Calificación</h2><button class="btn icon ghost" type="button" data-strip="grade-close" aria-label="Cerrar el panel">${ICON.close}</button></header>
    <p class="gp-who"><b>${e(s.student.name)}</b> <span class="state ${st.cls}">${e(st.label)}</span></p>
    ${s.submittedAt ? `<p class="muted small">Entregada: ${e(fmtDateTime(s.submittedAt))}${s.late ? ' (fuera de la fecha límite)' : ''}</p>` : ''}
    <p class="notice warn">${archivedHtml('review', 'El aula está archivada: desarchívala para calificar o devolver.')}</p>
    <h3 class="gp-h">Calificación</h3>
    <p class="gp-value">${s.grade !== null && s.grade !== undefined ? e(gradeText(s.grade, a.points)) : '<span class="muted">Sin calificación</span>'}</p>
    <h3 class="gp-h">Comentario para el alumno</h3>
    ${s.comment ? `<div class="text gp-value">${linkify(s.comment)}</div>` : '<p class="muted gp-value">Sin comentario.</p>'}
    ${s.gradedAt ? `<p class="muted small">Última calificación: ${e(fmtDateTime(s.gradedAt))}</p>` : ''}
  </div>`;
}

async function gradeSubmission(app, action) {
  const form = $('#grade-form');
  const err = form.querySelector('.form-error');
  err.textContent = '';
  const points = Number(task.assignment.points);
  const raw = $('#gp-grade').value.trim().replace(',', '.');
  let grade = null;
  if (raw !== '') {
    grade = Number(raw);
    if (!Number.isFinite(grade) || grade < 0 || grade > points) {
      err.textContent = `La calificación va de 0 a ${fmtNum(points)}.`;
      $('#gp-grade').focus();
      return;
    }
    grade = Math.round(grade * 10) / 10;
  }
  const comment = $('#gp-comment').value.trim();
  if (action === 'return' && !comment && !confirm('¿Devolver sin comentario? Al alumno le sirve saber qué corregir.')) return;
  const buttons = form.querySelectorAll('button');
  buttons.forEach((b) => (b.disabled = true));
  try {
    const s = await api.post(`/api/submissions/${task.sub.id}/grade`, { grade, comment, action });
    task.sub = { ...task.sub, ...s, project: undefined };
    const item = task.list.find((x) => x.id === s.id);
    if (item) Object.assign(item, s);
    $('#grade-panel').dataset.sid = '';
    render(app);
    app.toast(action === 'return' ? `Devuelta a ${task.sub.student.name} para corregir.` : `Calificación guardada para ${task.sub.student.name}.`);
  } catch (e) {
    buttons.forEach((b) => (b.disabled = false));
    err.textContent = e.message;
    if (e.status === 409) refreshReview(app, e.message);
  }
}

/** 409 al calificar: la entrega o el aula cambiaron (el alumno anuló la entrega, o el aula se archivó). Se vuelve a leer. */
async function refreshReview(app, message) {
  let s;
  try {
    s = await api.get(`/api/submissions/${task.sub.id}`);
  } catch {
    return; // queda el error en el panel
  }
  if (s.state === task.sub.state && s.assignment.classArchived === !!task.assignment.classArchived) return;
  task.sub = { ...task.sub, ...s, project: undefined };
  task.assignment = s.assignment;
  render(app);
  app.toast(message, true);
}
